/**
 * VestibularCard — третият вход за равновесие + триаж на замайването (Nerve3).
 * POST /api/neuro-twin/vestibular-exam, POST /api/neuro-twin/dizziness
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Ear, Loader2, ShieldAlert, Siren } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEAK_COLOR = {
  balanced: "text-emerald-500", visual_dependent: "text-amber-500",
  proprioceptive: "text-amber-500", vestibular: "text-rose-500",
  mixed: "text-rose-500",
};

const Pills = ({ opts, value, onPick, name, multi = false }) => (
  <div className="mt-1 flex flex-wrap gap-1.5">
    {opts.map((o) => {
      const on = multi ? (value || []).includes(o.key) : value === o.key;
      return (
        <button key={o.key} type="button" onClick={() => onPick(o.key)}
          className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
            on ? "border-violet-500/50 bg-violet-500/15" : "border-current/20 opacity-70"}`}
          data-testid={`vest-${name}-${o.key}`}>
          {o.label_bg}
        </button>
      );
    })}
  </div>
);

export const VestibularCard = ({ d, setD, cardCls, border }) => {
  const v = d?.vestibular;
  const p = v?.protocol;
  const [tab, setTab] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ex, setEx] = useState({ foam_romberg: 20, gaze_stability: "clear", head_turn_gait: "steady", fukuda: "skipped" });
  const [dz, setDz] = useState({ type: "none", duration: "seconds", triggers: [], ear: [], red_flags: [] });
  if (!p) return null;

  const toggle = (field, key) => setDz((s) => ({
    ...s, [field]: s[field].includes(key) ? s[field].filter((x) => x !== key) : [...s[field], key],
  }));

  const post = async (url, body) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/${url}`, body);
      setD(data); setTab(null); toast.success("Записано");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };

  const three = v.three_inputs;
  const dizzy = v.dizziness;

  return (
    <div className={cardCls} data-testid="neuro-vestibular-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Ear className="w-4 h-4 text-violet-500" /> Вестибуларен вход и замайване
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-60" />}
          </div>
          <p className="text-[11px] opacity-75 mt-1">{v.explain_bg}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button type="button" onClick={() => setTab(tab === "exam" ? null : "exam")}
            className="px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold"
            data-testid="vest-exam-toggle">
            {three ? "Нов тест" : "Тест на трите входа"}
          </button>
          <button type="button" onClick={() => setTab(tab === "dizzy" ? null : "dizzy")}
            className={`px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border ${border}`}
            data-testid="vest-dizzy-toggle">
            Замайване
          </button>
        </div>
      </div>

      {dizzy?.red_flags?.length > 0 && (
        <div className="mt-3 p-2.5 rounded-xl bg-rose-500/15 border border-rose-500/50"
          data-testid="vest-red-flags">
          <div className="text-[12px] font-bold text-rose-600 dark:text-rose-300 inline-flex items-center gap-1.5">
            <Siren className="w-4 h-4" /> Това е спешно
          </div>
          <p className="text-[11px] mt-1 font-medium">{dizzy.red_flag_text_bg}</p>
        </div>
      )}

      {three && (
        <div className={`mt-3 p-2 rounded-lg border ${border}`} data-testid="vest-three-inputs">
          <div className={`text-[12px] font-bold ${WEAK_COLOR[three.weakest]}`}
            data-testid="vest-weakest">{three.label_bg} ({three.date})</div>
          <p className="text-[11px] opacity-80 mt-0.5">{three.text_bg}</p>
          {three.details.map((x, i) => (
            <div key={i} className="mt-1.5">
              <div className="text-[11px] font-bold">{x.label_bg}</div>
              <div className="text-[11px] opacity-75">{x.text_bg}</div>
            </div>
          ))}
        </div>
      )}

      {dizzy && (
        <div className={`mt-2 p-2 rounded-lg border ${border}`} data-testid="vest-dizzy-result">
          <div className="text-[12px] font-bold" data-testid="vest-dizzy-pattern">{dizzy.title_bg}</div>
          <p className="text-[11px] opacity-80 mt-0.5">{dizzy.text_bg}</p>
          {dizzy.autonomic_bg && (
            <p className="text-[11px] opacity-80 mt-1" data-testid="vest-autonomic">{dizzy.autonomic_bg}</p>
          )}
          <p className="text-[11px] font-medium mt-1">{dizzy.action_bg}</p>
          <div className="text-[10px] opacity-60 mt-0.5">Води към: {dizzy.twin_bg} близнак</div>
        </div>
      )}

      {v.ototoxic_meds?.length > 0 && (
        <p className="text-[11px] mt-2 inline-flex items-start gap-1.5 text-amber-600 dark:text-amber-400"
          data-testid="vest-ototoxic">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {v.ototoxic_meds.map((m) => `${m.name} (${m.group_bg})`).join(", ")} — {v.ototoxic_text_bg}
        </p>
      )}

      {tab === "exam" && (
        <div className="mt-3" data-testid="vest-exam-form">
          <div className="p-2 rounded-lg border border-amber-500/40 bg-amber-500/10">
            <div className="text-[12px] font-bold inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <ShieldAlert className="w-3.5 h-3.5" /> Преди да станете
            </div>
            <ul className="mt-1 space-y-0.5 list-disc pl-4">
              {p.safety_bg.map((s, i) => <li key={i} className="text-[11px] opacity-90">{s}</li>)}
            </ul>
          </div>

          <div className={`mt-2 p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">{p.foam.label_bg}</div>
            <div className="text-[10px] opacity-70 mt-0.5">{p.foam.how_bg}</div>
            <input type="number" min={0} max={30} value={ex.foam_romberg}
              onChange={(e) => setEx((s) => ({ ...s, foam_romberg: Math.max(0, Math.min(30, parseInt(e.target.value || 0, 10) || 0)) }))}
              className={`mt-1.5 w-24 px-2 py-1 rounded-md border bg-transparent text-[12px] ${border}`}
              data-testid="vest-foam-input" />
          </div>

          <div className={`mt-2 p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Стабилност на погледа (VOR)</div>
            <div className="text-[10px] opacity-70 mt-0.5">{p.gaze_how_bg}</div>
            <Pills opts={p.gaze_opts} value={ex.gaze_stability} name="gaze"
              onPick={(k) => setEx((s) => ({ ...s, gaze_stability: k }))} />
          </div>

          <div className={`mt-2 p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Ходене с обръщане на главата</div>
            <div className="text-[10px] opacity-70 mt-0.5">{p.gait_how_bg}</div>
            <Pills opts={p.gait_opts} value={ex.head_turn_gait} name="gait"
              onPick={(k) => setEx((s) => ({ ...s, head_turn_gait: k }))} />
          </div>

          {v.fukuda_allowed ? (
            <div className={`mt-2 p-2 rounded-lg border ${border}`}>
              <div className="text-[12px] font-bold">Тест на Fukuda</div>
              <div className="text-[10px] opacity-70 mt-0.5">{p.fukuda_how_bg}</div>
              <Pills opts={p.fukuda_opts} value={ex.fukuda} name="fukuda"
                onPick={(k) => setEx((s) => ({ ...s, fukuda: k }))} />
            </div>
          ) : (
            <p className="text-[11px] mt-2 text-amber-600 dark:text-amber-400" data-testid="vest-fukuda-hidden">
              Тестът на Fukuda е скрит заради Вашия риск от падане — ходене на
              място със затворени очи не е безопасно при падания.
            </p>
          )}

          <button type="button" onClick={() => post("vestibular-exam", ex)} disabled={busy}
            className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold disabled:opacity-60"
            data-testid="vest-exam-save">Запиши теста</button>
        </div>
      )}

      {tab === "dizzy" && (
        <div className="mt-3 space-y-2" data-testid="vest-dizzy-form">
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Какъв е характерът на замайването?</div>
            <Pills opts={p.dizzy_types} value={dz.type} name="type"
              onPick={(k) => setDz((s) => ({ ...s, type: k }))} />
          </div>
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Колко време държи?</div>
            <Pills opts={p.dizzy_duration} value={dz.duration} name="dur"
              onPick={(k) => setDz((s) => ({ ...s, duration: k }))} />
          </div>
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Кога се появява?</div>
            <Pills opts={p.dizzy_triggers} value={dz.triggers} name="trig" multi
              onPick={(k) => toggle("triggers", k)} />
          </div>
          <div className={`p-2 rounded-lg border ${border}`}>
            <div className="text-[12px] font-bold">Ухо и съпътстващи</div>
            <Pills opts={p.dizzy_ear} value={dz.ear} name="ear" multi
              onPick={(k) => toggle("ear", k)} />
          </div>
          <div className="p-2 rounded-lg border border-rose-500/40 bg-rose-500/5">
            <div className="text-[12px] font-bold text-rose-600 dark:text-rose-300">
              Има ли нещо от това? (спешни признаци)
            </div>
            <Pills opts={p.red_flags} value={dz.red_flags} name="flag" multi
              onPick={(k) => toggle("red_flags", k)} />
          </div>
          <button type="button" onClick={() => post("dizziness", dz)} disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold disabled:opacity-60"
            data-testid="vest-dizzy-save">Запиши</button>
        </div>
      )}

      <p className="text-[9px] opacity-50 mt-2">{v.note_bg}</p>
    </div>
  );
};

export default VestibularCard;
